import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductsThunk } from '@spike/products-action';
import { Product } from '@spike/product-model';
import { RootState } from 'store';
import { useApiClientWrapper, useMasterData } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { Spinner } from 'components/UI';
import Pagination from 'components/UI/Pagination';
import ProductsHeader from 'components/Products/Header/ProductsHeader';
import Button from 'components/UI/V3/Button/Button';
import Status from './Status';
import StockSidebar from './StockSidebar';
import LotsSidebar from './LotsSidebar';
import { NoProductsIllustration } from './NoProductsIllustration';
import { Img } from 'react-image';
import isEmpty from 'lodash/isEmpty';
import styles from './Stock.module.scss';

const EMPTY_INVENTORY_STATUS_IDS: Array<string> = [];

const isInventoryTrackingEnabled = (product: Product): boolean =>
    product.businessProduct?.inventory?.inventoryTracking !== false;

const getTrackingLabel = (product: Product): string =>
    product.businessProduct?.inventory?.trackBatches ? 'Track by Lots' : 'Track Total Stock';

const formatQuantity = (product: Product): string | number => {
    const stock = product.businessProduct?.stock;

    if (stock === undefined || stock === null || stock === '') {
        return '-';
    }

    return stock;
};

const formatLowStock = (product: Product): string | number => {
    const inventory = product.businessProduct?.inventory;

    if (!inventory?.lowStockNotifications) {
        return '-';
    }

    if (inventory.lowStockThreshold === undefined || inventory.lowStockThreshold === null) {
        return '-';
    }

    return inventory.lowStockThreshold;
};

const getProductName = (product: Product): string => {
    if (isEmpty(product.businessProduct?.name)) {
        return product.name || '';
    }
    return product.businessProduct?.name || '';
};

interface StockProps {
    onAddProduct: () => void;
}

export const Stock: FunctionComponent<StockProps> = ({ onAddProduct }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const masterData = useMasterData();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const isMobile = useMediaQuery('(max-width:767.95px)');
    const searchTextRef = useRef('');
    const [searchText, setSearchText] = useState('');
    const [selectedStatusIds, setSelectedStatusIds] = useState<Array<string>>([]);
    const [selectedMarketplaceIds, setSelectedMarketplaceIds] = useState<Array<number>>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();

    const products = useSelector<RootState, Array<Product>>(state => state.products.list.page ?? []);
    const totalRecords = useSelector<RootState, number>(state => state.products.list.pagination?.totalRecords ?? 0);
    const loading = useSelector<RootState, boolean>(state => state.products.loading);

    const inventoryStatusIds = useMemo(() => {
        const allStatusIds = (masterData.inventory?.statuses ?? []).map(status => status.id);
        const isAllStatusesSelected =
            selectedStatusIds.length === 0 ||
            (allStatusIds.length > 0 && allStatusIds.every(id => selectedStatusIds.includes(id)));

        return isAllStatusesSelected ? EMPTY_INVENTORY_STATUS_IDS : selectedStatusIds;
    }, [selectedStatusIds, masterData.inventory?.statuses]);

    const marketplaceIdsKey = organizationMarketplaces.map(marketplace => marketplace.id).join(',');
    const allMarketplaceIds = useMemo(
        () =>
            organizationMarketplaces.map(marketplace => marketplace.id).filter((id): id is number => id !== undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [marketplaceIdsKey]
    );

    const marketplaceIds = useMemo(() => {
        const isAllLocationsSelected =
            selectedMarketplaceIds.length === 0 ||
            (allMarketplaceIds.length > 0 && allMarketplaceIds.every(id => selectedMarketplaceIds.includes(id)));

        return isAllLocationsSelected ? allMarketplaceIds : selectedMarketplaceIds;
    }, [selectedMarketplaceIds, allMarketplaceIds]);

    useEffect(() => {
        dispatch(
            fetchProductsThunk(
                apiClientWrapper,
                searchTextRef.current,
                inventoryStatusIds,
                marketplaceIds,
                [],
                page,
                pageSize
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, inventoryStatusIds, marketplaceIds]);

    const handleSearch = (text: string) => {
        if (text === searchTextRef.current) {
            return;
        }

        searchTextRef.current = text;
        setSearchText(text);

        if (page !== 1) {
            setPage(1);
            return;
        }

        dispatch(fetchProductsThunk(apiClientWrapper, text, inventoryStatusIds, marketplaceIds, [], 1, pageSize));
    };

    const handleSelectStockStatuses = (ids: Array<string>) => {
        setSelectedStatusIds(ids);
        setPage(1);
    };

    const handleSelectStockMarketplaces = (ids: Array<number>) => {
        setSelectedMarketplaceIds(ids);
        setPage(1);
    };

    const changePageSizeHandler = (nextPageSize: number) => {
        setPage(1);
        setPageSize(nextPageSize);
    };

    const handleOpenProductStock = (product: Product) => {
        if (!isInventoryTrackingEnabled(product)) {
            return;
        }

        setSelectedProduct(product);
    };

    const handleCloseProductStock = () => {
        setSelectedProduct(undefined);
    };

    const refetchProducts = () => {
        dispatch(
            fetchProductsThunk(
                apiClientWrapper,
                searchTextRef.current,
                inventoryStatusIds,
                marketplaceIds,
                [],
                page,
                pageSize
            )
        );
    };

    const isLocationFilterActive =
        selectedMarketplaceIds.length > 0 && allMarketplaceIds.some(id => !selectedMarketplaceIds.includes(id));
    const isNoProductsRegistered =
        !searchText && selectedStatusIds.length === 0 && !isLocationFilterActive && products.length === 0;

    const noProductsEmpty = (
        <div className={styles.noProducts}>
            <NoProductsIllustration className={styles.noProductsImage} />
            <h2 className={styles.noProductsTitle}>No Products Found</h2>
            <p className={styles.noProductsDescription}>
                Add your first product to start
                <br />
                tracking stock quantities.
            </p>
            <div className={styles.noProductsButton}>
                <Button
                    label="Add Product"
                    size="large"
                    variant="primary"
                    leftIcon={faPlus}
                    onClick={onAddProduct}
                />
            </div>
        </div>
    );

    const filterEmpty = (
        <div className={styles.filterEmpty}>
            <div className={styles.filterEmptyTitle}>Not Found!</div>
            <div className={styles.filterEmptyDescription}>Products not found.</div>
        </div>
    );

    const emptyContent = isNoProductsRegistered ? noProductsEmpty : filterEmpty;

    const mobileList = (
        <div className={styles.mobileList}>
            {products.length === 0 ? (
                <div className={styles.noProductsFill}>{emptyContent}</div>
            ) : (
                products.map(product => (
                    <div
                        key={product.uuid || product.id}
                        className={`${styles.mobileCard}${
                            isInventoryTrackingEnabled(product) ? '' : ` ${styles.mobileCardDisabled}`
                        }`}
                        onClick={() => handleOpenProductStock(product)}
                    >
                        <div className={styles.mobileName}>{getProductName(product)}</div>
                        <div className={styles.mobileBarcodeStatus}>
                            {isInventoryTrackingEnabled(product) && (
                                <span className={styles.codeText}>{getTrackingLabel(product)}</span>
                            )}
                            {product.businessProduct?.inventory?.status ? (
                                <Status status={product.businessProduct.inventory.status} />
                            ) : (
                                <span className={styles.cellText}>-</span>
                            )}
                        </div>
                        <div className={styles.mobileBottom}>
                            <div className={styles.mobileMetrics}>
                                <span className={styles.mobileMetricText}>Quantity: {formatQuantity(product)}</span>
                                <span className={styles.mobileMetricText}>Low Stock: {formatLowStock(product)}</span>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const tableHead = (
        <TableHead>
            <TableRow>
                <TableCell className={`${styles.headerCell} ${styles.productNameCell}`}>Product</TableCell>
                <TableCell className={`${styles.headerCell} ${styles.statusCell}`}>Status</TableCell>
                <TableCell className={`${styles.headerCell} ${styles.lowStockCell}`}>Low Stock</TableCell>
                <TableCell className={`${styles.headerCell} ${styles.quantityCell}`}>Quantity</TableCell>
            </TableRow>
        </TableHead>
    );

    const desktopTable =
        products.length === 0 ? (
            <div className={styles.emptyTableLayout}>
                <TableContainer className={styles.tableHeaderOnly}>
                    <Table className={styles.table}>{tableHead}</Table>
                </TableContainer>
                <div className={styles.noProductsFill}>{emptyContent}</div>
            </div>
        ) : (
            <TableContainer className={styles.tableContainer}>
                <Table className={styles.table}>
                    {tableHead}
                    <TableBody>
                        {products.map(product => (
                            <TableRow
                                key={product.uuid || product.id}
                                className={`${styles.row}${
                                    isInventoryTrackingEnabled(product) ? '' : ` ${styles.rowDisabled}`
                                }`}
                                onClick={() => handleOpenProductStock(product)}
                            >
                                <TableCell className={`${styles.cell} ${styles.productNameCell}`}>
                                    <div className={styles.productInfo}>
                                        <div className={styles.imageBox}>
                                            {product.imageUrl ? (
                                                <Img
                                                    src={product.imageUrl}
                                                    className={styles.imageBox}
                                                />
                                            ) : (
                                                <div className={styles.uploadBox}>
                                                    <FontAwesomeIcon
                                                        icon={faCamera}
                                                        className={styles.uploadIcon}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.nameContainer}>
                                            <span className={styles.cellText}>{getProductName(product)}</span>
                                            {isInventoryTrackingEnabled(product) && (
                                                <span className={styles.codeText}>{getTrackingLabel(product)}</span>
                                            )}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className={`${styles.cell} ${styles.statusCell}`}>
                                    {product.businessProduct?.inventory?.status ? (
                                        <Status status={product.businessProduct.inventory.status} />
                                    ) : (
                                        <span className={styles.cellText}>-</span>
                                    )}
                                </TableCell>
                                <TableCell className={`${styles.cell} ${styles.lowStockCell}`}>
                                    <span className={styles.cellText}>{formatLowStock(product)}</span>
                                </TableCell>
                                <TableCell className={`${styles.cell} ${styles.quantityCell}`}>
                                    <span className={styles.cellText}>{formatQuantity(product)}</span>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );

    return (
        <>
            <div className={styles.root}>
                <div className={styles.headerSection}>
                    <ProductsHeader
                        selectedViewId="stock"
                        vendorsActive={false}
                        vendorsDeactive={false}
                        products={products}
                        vendors={[]}
                        categories={[]}
                        selectedStockStatuses={selectedStatusIds}
                        selectedStockMarketplaceIds={selectedMarketplaceIds}
                        onSearch={handleSearch}
                        onAddProduct={onAddProduct}
                        onAddVendor={() => undefined}
                        onAddCategory={() => undefined}
                        onAddStock={() => undefined}
                        onSelectProductCategory={() => undefined}
                        onSelectVendor={() => undefined}
                        onSelectStockStatuses={handleSelectStockStatuses}
                        onSelectStockMarketplaces={handleSelectStockMarketplaces}
                        loading={loading}
                    />
                </div>
                <div className={styles.tableWrapper}>
                    {loading ? (
                        <div className={styles.spinnerWrapper}>
                            <Spinner />
                        </div>
                    ) : isMobile ? (
                        mobileList
                    ) : (
                        desktopTable
                    )}
                </div>
                {!loading && totalRecords > 0 && (
                    <Pagination
                        className={styles.pagination}
                        count={totalRecords}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={changePageSizeHandler}
                    />
                )}
            </div>
            {selectedProduct &&
                (selectedProduct.businessProduct?.inventory?.trackBatches ? (
                    <LotsSidebar
                        product={products.find(product => product.id === selectedProduct.id) ?? selectedProduct}
                        onClose={handleCloseProductStock}
                        onSaved={refetchProducts}
                    />
                ) : (
                    <StockSidebar
                        product={products.find(product => product.id === selectedProduct.id) ?? selectedProduct}
                        onClose={handleCloseProductStock}
                        onSaved={refetchProducts}
                    />
                ))}
        </>
    );
};

export default Stock;
